// -----------------------------------------------------------------------------
// ContactDashboard.jsx   (all-in-one contacts + comm-logs manager)
// FULL file — now supports “ranking” (integer) and “tags” (chip list)
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Avatar,
  IconButton,
  Typography,
  Grid,
  TextField,
  Button,
  Collapse,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
} from "@mui/material";

import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  DeleteOutline as DeleteOutlineIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Call as CallIcon,
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

import { useQuery, useMutation, useQueryClient } from "react-query";
import API from "../../API";
import AlertDialog from "../../Components/AlertDialog";
import useAlertDialog from "../../Components/useAlertDialog";

/* ─────────────────── helpers ─────────────────── */
const iso = (d) =>
  new Date(d).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
const label = (f) =>
  f.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

/* ─────────────────── chip input for tags ─────────────────── */
function TagsChipInput({ value = [], onChange, labelText = "Tags" }) {
  const [draft, setDraft] = useState("");
  const addTag = (tagStr) => {
    const t = (tagStr ?? draft).trim();
    if (!t) return;
    if (value.includes(t)) return;
    onChange([...value, t]);
    setDraft("");
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
  };
  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        label={labelText}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        fullWidth
      />
      <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
        {value.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            onDelete={() => onChange(value.filter((t) => t !== tag))}
          />
        ))}
      </Box>
    </Box>
  );
}

/* ─────────────────── main component ─────────────────── */
export default function ContactDashboard() {
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down("md"));
  const qc = useQueryClient();
  const { dialogState } = useAlertDialog();

  /* ───── contacts CRUD ───── */
  const {
    data: contacts = [],
    isLoading: isContactsLoading,
    error: contactsErr,
  } = useQuery(["contacts"], API.contacts.list, {
    refetchOnWindowFocus: false,
  });

  const selfContact = useMemo(
    () => contacts.find((c) => c.contact_type === "self"),
    [contacts]
  );
  const otherContacts = useMemo(
    () => contacts.filter((c) => c.contact_type !== "self"),
    [contacts]
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [editing, setEditing] = useState({});
  const [openAdd, setOpenAdd] = useState(false);
  const [newContact, setNewContact] = useState({
    profile_photo_link: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    title: "",
    location: "",
    notes: "",
    ranking: "",
    tags: [],
  });
  const [openSelf, setOpenSelf] = useState(false);

  useEffect(() => {
    const map = {};
    contacts.forEach((c) => {
      map[c.id] = { ...c, __open: false };
    });
    setEditing(map);
  }, [contacts]);

  const updMut = useMutation(({ id, data }) => API.contacts.update(id, data), {
    onSuccess: () => qc.invalidateQueries(["contacts"]),
  });
  const delMut = useMutation((id) => API.contacts.remove(id), {
    onSuccess: () => qc.invalidateQueries(["contacts"]),
  });
  const addMut = useMutation((data) => API.contacts.create(data), {
    onSuccess: () => {
      qc.invalidateQueries(["contacts"]);
      setOpenAdd(false);
    },
  });

  /* search (includes ranking + tags) */
  const matchSearch = (c) => {
    const fields = [
      c.first_name,
      c.last_name,
      c.email,
      c.phone,
      c.company,
      c.location,
      c.notes,
      String(c.ranking ?? ""),
      ...(c.tags || []),
    ];
    return fields.some((f) => f?.toString().toLowerCase().includes(searchTerm));
  };
  const filtContacts = otherContacts.filter(matchSearch);

  /* ───── comm-logs ───── */
  const { data: summary, isLoading: sumLoad } = useQuery(
    ["commSummary"],
    API.contacts.logs.summary
  );
  const { data: logs = [], isLoading: logsLoad } = useQuery(
    ["commLogs"],
    API.contacts.logs.listAll
  );

  const [openLogDlg, setOpenLogDlg] = useState(false);
  const [logForm, setLogForm] = useState({
    id: null,
    contact_id: "",
    datetime: "",
    notes: "",
    rating: 3,
  });
  const createLogMut = useMutation((input) => API.contacts.logs.create(input), {
    onSuccess: () => {
      qc.invalidateQueries(["commLogs"]);
      qc.invalidateQueries(["commSummary"]);
      setOpenLogDlg(false);
    },
  });
  const updLogMut = useMutation(
    ({ id, data }) => API.contacts.logs.update(id, data),
    {
      onSuccess: () => {
        qc.invalidateQueries(["commLogs"]);
        qc.invalidateQueries(["commSummary"]);
        setOpenLogDlg(false);
      },
    }
  );
  const delLogMut = useMutation((id) => API.contacts.logs.remove(id), {
    onSuccess: () => {
      qc.invalidateQueries(["commLogs"]);
      qc.invalidateQueries(["commSummary"]);
    },
  });

  const openForCreate = () => {
    setLogForm({
      id: null,
      contact_id: "",
      datetime: new Date().toISOString(),
      notes: "",
      rating: 3,
    });
    setOpenLogDlg(true);
  };
  const openForEdit = (log) => {
    setLogForm({
      id: log.id,
      contact_id: log.contact_id,
      datetime: log.datetime_of_call,
      notes: log.notes || "",
      rating: log.rating || 3,
    });
    setOpenLogDlg(true);
  };

  /* ───── render guards ───── */
  if (isContactsLoading || sumLoad || logsLoad)
    return <CircularProgress sx={{ mt: 4, mx: "auto" }} />;
  if (contactsErr)
    return <Typography color="error">Failed to load contacts</Typography>;

  /* ───── JSX ───── */
  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, md: 4 } }}>
      <AlertDialog alertState={dialogState} />

      {/* ───────────────── COMM SUMMARY ───────────────── */}
      <Card elevation={4} sx={{ mb: 4 }}>
        <CardHeader
          avatar={<CallIcon color="primary" />}
          title="Communication Summary"
          action={
            <Button startIcon={<AddIcon />} onClick={openForCreate}>
              Add Call/Meeting
            </Button>
          }
        />
        <Divider />
        <CardContent>
          {summary?.total_calls ? (
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="h5" fontWeight={700}>
                  {summary.total_calls}
                </Typography>
                <Typography variant="body2">Total Calls</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="h5" fontWeight={700}>
                  {summary.average_rating.toFixed(1)}
                </Typography>
                <Typography variant="body2">Avg Rating</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Most recent:</Typography>
                <Typography variant="body2">
                  {summary.most_recent_call
                    ? iso(summary.most_recent_call)
                    : "—"}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body1">
              Make some calls to get going!
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* ───────────────── ALL CALL LOGS ───────────────── */}
      {logs.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            All Call Logs
          </Typography>
          <Card elevation={2} sx={{ mb: 4 }}>
            <CardContent sx={{ p: 0 }}>
              <Box
                component="table"
                sx={{ width: "100%", borderCollapse: "collapse" }}
              >
                <thead>
                  <tr>
                    <th style={{ padding: "12px 8px", textAlign: "left" }}>
                      Date
                    </th>
                    <th style={{ padding: "12px 8px" }}>Contact</th>
                    <th style={{ padding: "12px 8px" }}>Rating</th>
                    <th style={{ padding: "12px 8px" }}>Notes</th>
                    <th style={{ padding: "12px 8px" }} />
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => {
                    const c =
                      contacts.find((ct) => ct.id === l.contact_id) || {};
                    return (
                      <tr key={l.id} style={{ borderTop: "1px solid #eee" }}>
                        <td style={{ padding: "12px 8px" }}>
                          {iso(l.datetime_of_call)}
                        </td>
                        <td style={{ padding: "12px 8px" }}>
                          {c.full_name || "—"}
                        </td>
                        <td style={{ padding: "12px 8px" }}>
                          {l.rating || "—"}
                        </td>
                        <td style={{ padding: "12px 8px" }}>
                          {l.notes?.slice(0, 60)}
                        </td>
                        <td style={{ padding: "12px 8px" }}>
                          <IconButton
                            size="small"
                            onClick={() => openForEdit(l)}
                          >
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => delLogMut.mutate(l.id)}
                          >
                            <DeleteForeverIcon
                              fontSize="inherit"
                              color="error"
                            />
                          </IconButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* ───────────────── SEARCH BAR ───────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <TextField
          size="small"
          placeholder="Search Contacts"
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1 }} color="action" />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
          sx={{ flex: "1 1 300px" }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAdd(true)}
        >
          Add Contact
        </Button>
      </Box>

      {/* ───────────────── ROLODEX ───────────────── */}
      <Typography variant="h4" fontWeight={600} sx={{ mb: 2 }}>
        My Rolodex{" "}
        {filtContacts.length !== otherContacts.length
          ? `(${filtContacts.length}/${otherContacts.length})`
          : ""}
      </Typography>

      <Grid container spacing={3}>
        {filtContacts.length ? (
          filtContacts.map((c) => {
            const isOpen = editing[c.id]?.__open;
            return (
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <Card
                  elevation={3}
                  sx={{
                    transition: "transform .2s",
                    ":hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardHeader
                    avatar={
                      <Avatar src={editing[c.id]?.profile_photo_link}>
                        {c.first_name?.[0] || "C"}
                      </Avatar>
                    }
                    title={`${c.first_name} ${c.last_name}`}
                    subheader={
                      <>
                        {c.title && (
                          <Typography variant="body2">{c.title}</Typography>
                        )}
                        {c.company && (
                          <Typography variant="body2">{c.company}</Typography>
                        )}
                        {c.ranking !== null &&
                          c.ranking !== undefined &&
                          c.ranking !== "" && (
                            <Typography variant="body2">
                              Rank: {c.ranking}
                            </Typography>
                          )}
                        <Box
                          sx={{
                            mt: 0.5,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {(c.tags || []).map((t) => (
                            <Chip size="small" key={t} label={t} />
                          ))}
                        </Box>
                      </>
                    }
                    action={
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Delete">
                          <IconButton onClick={() => delMut.mutate(c.id)}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isOpen ? "Collapse" : "Expand"}>
                          <IconButton
                            onClick={() =>
                              setEditing((p) => ({
                                ...p,
                                [c.id]: {
                                  ...p[c.id],
                                  __open: !isOpen,
                                },
                              }))
                            }
                          >
                            <ExpandMoreIcon
                              sx={{
                                transform: isOpen
                                  ? "rotate(180deg)"
                                  : "rotate(0)",
                                transition: "transform .2s",
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                  />
                  <CardContent>
                    <Typography variant="body2">Email: {c.email}</Typography>
                    <Typography variant="body2">Phone: {c.phone}</Typography>
                    <Typography variant="body2">
                      Location: {c.location}
                    </Typography>
                    {c.notes && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {c.notes.slice(0, 100)}
                        {c.notes.length > 100 ? "…" : ""}
                      </Typography>
                    )}
                  </CardContent>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <CardContent>
                      <Grid container spacing={2}>
                        {[
                          "profile_photo_link",
                          "email",
                          "phone",
                          "location",
                          "notes",
                          "ranking",
                        ].map((f) => (
                          <Grid item xs={12} key={f}>
                            <TextField
                              label={label(f)}
                              type={
                                f === "ranking"
                                  ? "number"
                                  : f === "email"
                                  ? "email"
                                  : "text"
                              }
                              value={editing[c.id]?.[f] || ""}
                              onChange={(e) =>
                                setEditing((p) => ({
                                  ...p,
                                  [c.id]: {
                                    ...p[c.id],
                                    [f]: e.target.value,
                                  },
                                }))
                              }
                              fullWidth
                              multiline={f === "notes"}
                              minRows={f === "notes" ? 2 : 1}
                            />
                          </Grid>
                        ))}
                        <Grid item xs={12}>
                          <TagsChipInput
                            value={editing[c.id]?.tags || []}
                            onChange={(tags) =>
                              setEditing((p) => ({
                                ...p,
                                [c.id]: { ...p[c.id], tags },
                              }))
                            }
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions sx={{ p: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={() => {
                          const d = { ...editing[c.id] };
                          delete d.id;
                          delete d.__open;
                          updMut.mutate({ id: c.id, data: d });
                        }}
                      >
                        Save Changes
                      </Button>
                    </CardActions>
                  </Collapse>
                </Card>
              </Grid>
            );
          })
        ) : (
          <Typography sx={{ p: 2 }}>No contacts match your search.</Typography>
        )}
      </Grid>

      {/* ───────────────── ADD CONTACT ───────────────── */}
      <Dialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Contact</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[
              "profile_photo_link",
              "first_name",
              "last_name",
              "email",
              "phone",
              "company",
              "title",
              "location",
              "notes",
              "ranking",
            ].map((f) => (
              <Grid item xs={12} sm={f === "notes" ? 12 : 6} key={f}>
                <TextField
                  label={label(f)}
                  type={
                    f === "ranking"
                      ? "number"
                      : f === "email"
                      ? "email"
                      : "text"
                  }
                  value={newContact[f]}
                  onChange={(e) =>
                    setNewContact((p) => ({ ...p, [f]: e.target.value }))
                  }
                  fullWidth
                  multiline={f === "notes"}
                  minRows={f === "notes" ? 2 : 1}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <TagsChipInput
                value={newContact.tags}
                onChange={(tags) =>
                  setNewContact((p) => ({ ...p, tags }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() =>
              addMut.mutate({
                ...newContact,
                ranking: newContact.ranking || null,
                full_name: `${newContact.first_name} ${newContact.last_name}`.trim(),
              })
            }
          >
            {addMut.isLoading ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───────────────── CALL-LOG DIALOG ───────────────── */}
      <Dialog
        open={openLogDlg}
        onClose={() => setOpenLogDlg(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {logForm.id ? "Edit Call Log" : "Add Call Log"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="ctLbl">Contact</InputLabel>
                <Select
                  labelId="ctLbl"
                  label="Contact"
                  value={logForm.contact_id}
                  onChange={(e) =>
                    setLogForm((p) => ({ ...p, contact_id: e.target.value }))
                  }
                >
                  {contacts.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.full_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="datetime-local"
                label="Date / Time"
                fullWidth
                value={logForm.datetime.slice(0, 16)}
                onChange={(e) =>
                  setLogForm((p) => ({
                    ...p,
                    datetime: new Date(e.target.value).toISOString(),
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                label="Rating (1-5)"
                fullWidth
                inputProps={{ min: 1, max: 5 }}
                value={logForm.rating}
                onChange={(e) =>
                  setLogForm((p) => ({
                    ...p,
                    rating: parseInt(e.target.value || 3, 10),
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                multiline
                minRows={3}
                fullWidth
                value={logForm.notes}
                onChange={(e) =>
                  setLogForm((p) => ({ ...p, notes: e.target.value }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLogDlg(false)}>Cancel</Button>
          {logForm.id && (
            <Button
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={() => {
                delLogMut.mutate(logForm.id);
                setOpenLogDlg(false);
              }}
            >
              Delete
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() =>
              logForm.id
                ? updLogMut.mutate({
                    id: logForm.id,
                    data: {
                      contact_id: logForm.contact_id,
                      datetime: logForm.datetime,
                      notes: logForm.notes,
                      rating: logForm.rating,
                    },
                  })
                : createLogMut.mutate({
                    contact_id: logForm.contact_id,
                    datetime: logForm.datetime,
                    notes: logForm.notes,
                    rating: logForm.rating,
                  })
            }
          >
            {logForm.id
              ? updLogMut.isLoading
                ? "Saving…"
                : "Save"
              : createLogMut.isLoading
              ? "Saving…"
              : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───────────────── SELF CONTACT DIALOG ───────────────── */}
      {selfContact && (
        <Dialog
          open={openSelf}
          onClose={() => setOpenSelf(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>My Contact Card</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {[
                "profile_photo_link",
                "first_name",
                "last_name",
                "email",
                "phone",
                "location",
                "notes",
                "ranking",
              ].map((f) => (
                <Grid
                  item
                  xs={12}
                  sm={f === "notes" ? 12 : 6}
                  key={f}
                >
                  <TextField
                    label={label(f)}
                    type={
                      f === "ranking"
                        ? "number"
                        : f === "email"
                        ? "email"
                        : "text"
                    }
                    fullWidth
                    multiline={f === "notes"}
                    minRows={f === "notes" ? 2 : 1}
                    value={editing[selfContact.id]?.[f] || ""}
                    onChange={(e) =>
                      setEditing((p) => ({
                        ...p,
                        [selfContact.id]: {
                          ...p[selfContact.id],
                          [f]: e.target.value,
                        },
                      }))
                    }
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <TagsChipInput
                  value={editing[selfContact.id]?.tags || []}
                  onChange={(tags) =>
                    setEditing((p) => ({
                      ...p,
                      [selfContact.id]: {
                        ...p[selfContact.id],
                        tags,
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSelf(false)}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => {
                const d = { ...editing[selfContact.id] };
                delete d.id;
                updMut.mutate({ id: selfContact.id, data: d });
                setOpenSelf(false);
              }}
            >
              Save
            </Button>
            <Button
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => {
                delMut.mutate(selfContact.id);
                setOpenSelf(false);
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ───────────────── FLOATING SELF BUTTON ───────────────── */}
      {selfContact && (
        <IconButton
          onClick={() => setOpenSelf(true)}
          sx={{
            position: "fixed",
            bottom: theme.spacing(2),
            left: theme.spacing(2),
            bgcolor: "background.paper",
            boxShadow: 3,
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          <PersonIcon color="action" />
        </IconButton>
      )}
    </Box>
  );
}
